import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import { Alert, CircularProgress, Snackbar } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ReactNode, useCallback, useMemo, useState } from 'react';

import { DEFAULT_DURATION, SnackbarContext, SnackbarContextType, SnackbarSeverity } from './SnackbarContext.types';

export type { SnackbarContextType, SnackbarSeverity } from './SnackbarContext.types';

interface SnackbarProviderProps {
  children: ReactNode;
}

export function SnackbarProvider({ children }: SnackbarProviderProps) {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState<SnackbarSeverity>('info');
  const [duration, setDuration] = useState(DEFAULT_DURATION);

  const handleClose = useCallback((_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpen(false);
  }, []);

  const showSnackbar = useCallback((msg: string, sev: SnackbarSeverity = 'info', dur: number = DEFAULT_DURATION) => {
    setMessage(msg);
    setSeverity(sev);
    setDuration(dur);
    setOpen(true);
  }, []);

  const showSuccess = useCallback(
    (msg: string, dur: number = DEFAULT_DURATION) => {
      showSnackbar(msg, 'success', dur);
    },
    [showSnackbar],
  );

  const showError = useCallback(
    (msg: string, dur: number = DEFAULT_DURATION) => {
      showSnackbar(msg, 'error', dur);
    },
    [showSnackbar],
  );

  const showWarning = useCallback(
    (msg: string, dur: number = DEFAULT_DURATION) => {
      showSnackbar(msg, 'warning', dur);
    },
    [showSnackbar],
  );

  const showInfo = useCallback(
    (msg: string, dur: number = DEFAULT_DURATION) => {
      showSnackbar(msg, 'info', dur);
    },
    [showSnackbar],
  );

  const showLoading = useCallback(
    (msg: string) => {
      // Loading toasts auto-dismiss when content loads, so we use a longer duration
      // In practice, this should be dismissed manually when loading completes
      showSnackbar(msg, 'loading', 0); // 0 means no auto-dismiss
    },
    [showSnackbar],
  );

  const contextValue: SnackbarContextType = useMemo(
    () => ({
      showSnackbar,
      showSuccess,
      showError,
      showWarning,
      showInfo,
      showLoading,
    }),
    [showSnackbar, showSuccess, showError, showWarning, showInfo, showLoading],
  );

  // Memoize alert styles to prevent unnecessary re-renders
  const alertStyles = useMemo(() => {
    const baseStyles = {
      width: '100%',
      borderRadius: '4px',
      backgroundColor: theme.palette.background.paper,
      color: theme.hhglobal.neutral.superblack,
      boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.15)',
      borderTop: 'none',
      borderLeft: 'none',
      borderRight: 'none',
      borderBottom: 'none',
      padding: '12px 16px',
      '& .MuiAlert-message': {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        width: '100%',
        padding: 0,
        fontSize: '14px',
        lineHeight: '20px',
      },
      '& .MuiAlert-action': {
        padding: 0,
        marginLeft: '12px',
        alignItems: 'center',
        '& .MuiIconButton-root': {
          color: theme.hhglobal.neutral.superblack,
          padding: '4px',
          '&:hover': {
            backgroundColor: theme.hhglobal.neutral.gray50,
          },
        },
      },
    };

    if (severity === 'loading') {
      return {
        ...baseStyles,
        border: `1px solid ${theme.hhglobal.brand.secondary.popViolet}`,
        '& .MuiAlert-icon': {
          display: 'flex',
          alignItems: 'center',
          marginRight: 0,
          padding: 0,
        },
        '& .MuiAlert-action': {
          display: 'none', // No close button for loading
        },
      };
    }

    if (severity === 'info') {
      return {
        ...baseStyles,
        borderTop: `2px solid ${theme.hhglobal.utility.info[800]}`,
        '& .MuiAlert-icon': {
          color: theme.hhglobal.utility.info[800],
        },
      };
    }

    if (severity === 'success') {
      return {
        ...baseStyles,
        borderTop: `2px solid ${theme.hhglobal.utility.success[800]}`,
        '& .MuiAlert-icon': {
          color: theme.hhglobal.utility.success[800],
        },
      };
    }

    if (severity === 'warning') {
      return {
        ...baseStyles,
        borderTop: `2px solid ${theme.hhglobal.utility.warning[800]}`,
        '& .MuiAlert-icon': {
          color: theme.hhglobal.utility.warning[800],
        },
      };
    }

    if (severity === 'error') {
      return {
        ...baseStyles,
        borderTop: `2px solid ${theme.hhglobal.utility.error[800]}`,
        '& .MuiAlert-icon': {
          color: theme.hhglobal.utility.error[800],
        },
      };
    }

    return baseStyles;
  }, [severity, theme]);

  const renderIcon = () => {
    if (severity === 'loading') {
      // Use teal/dark blue color for loading spinner - using infoBlue[800] as teal/dark blue
      return (
        <CircularProgress
          size={20}
          sx={{
            color: theme.hhglobal.utility.info[800],
            flexShrink: 0,
          }}
        />
      );
    }
    if (severity === 'info') {
      return <InfoIcon sx={{ color: theme.hhglobal.utility.info[800] }} />;
    }
    if (severity === 'success') {
      return <CheckCircleIcon sx={{ color: theme.hhglobal.utility.success[800] }} />;
    }
    if (severity === 'warning') {
      return <WarningIcon sx={{ color: theme.hhglobal.utility.warning[800] }} />;
    }
    if (severity === 'error') {
      return <ErrorIcon sx={{ color: theme.hhglobal.utility.error[800] }} />;
    }
    return false; // Use default Material-UI icons for other severities
  };

  return (
    <SnackbarContext.Provider value={contextValue}>
      {children}
      <Snackbar
        open={open}
        autoHideDuration={duration === 0 ? null : duration}
        onClose={duration === 0 ? undefined : handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={severity === 'loading' ? undefined : handleClose}
          severity={severity === 'loading' ? 'info' : severity}
          icon={renderIcon()}
          sx={alertStyles}
          variant='filled'
        >
          {message}
        </Alert>
      </Snackbar>
    </SnackbarContext.Provider>
  );
}
